import { useMemo, useState } from "react";
import { useMonitoringActivityPayloads } from "../../data/useMonitoringActivityPayloads";
import { useMonitoringScreeningMatches } from "../../data/useMonitoringScreeningMatches";
import {
  buildMonitoringMatches,
  buildScreeningRecord,
  decisionCounts,
  informationUsedFromRecord,
  MonitoringMatch,
  orderMonitoringMatches,
  SCREENING_EVENT,
} from "../../utils/monitoringScreening";
import { useAuth } from "../../context/AuthContext";

export const ALL_ACTIVITY = "all" as const;
export type TimelineSelection = number | typeof ALL_ACTIVITY;
export type ResultsLens = "new" | "all";

/**
 * Everything the LexisNexis2 Monitoring record view renders, and the two pieces
 * of state that scope it: which timeline entry is selected, and whether the
 * results lens is showing only what has not been signed off.
 *
 * Kept out of the components because the derivation is the hard part and the
 * rendering is not.
 */
interface ControlledSelection {
  /** null means "the default", which is the newest alert. */
  selection: TimelineSelection | null;
  onSelect: (selection: TimelineSelection) => void;
}

export const useMonitoringScreeningModel = (
  monitoringId: number | string,
  monitoring: any,
  activities: any[] = [],
  // the activity history table can also select an alert, so the page owns the
  // selection and hands it down rather than the rail owning it privately
  controlled?: ControlledSelection,
) => {
  const screeningActivities = useMemo(
    () =>
      (activities || []).filter(
        (activity) => activity?.event === SCREENING_EVENT,
      ),
    [activities],
  );

  // raised by "Load them" on the rail. The limit has to live here rather than in
  // the rail, because raising it has to widen the fetch as well as the list, or
  // the extra entries appear with nothing behind them
  const [activityLimit, setActivityLimit] = useState<number | undefined>(
    undefined,
  );

  const { payloads, isLoading: isLoadingPayloads, notLoadedCount } =
    useMonitoringActivityPayloads(
      screeningActivities.map((activity) => activity.id),
      activityLimit,
    );

  const loadAllActivities = () =>
    setActivityLimit(screeningActivities.length);

  const { hasPermission } = useAuth();
  const canViewMatchList = hasPermission("screening.view");
  const {
    data: matchListData,
    isLoading: isMatchListPending,
    fetchStatus: matchListFetchStatus,
  } = useMonitoringScreeningMatches(monitoringId);
  // a disabled query stays isLoading in react-query v4, so only count it as
  // loading while it is actually fetching or the page would spin forever
  const isLoadingMatchList =
    isMatchListPending && matchListFetchStatus !== "idle";

  const accumulatedMatches = useMemo(
    () => matchListData?.data?.results ?? [],
    [matchListData],
  );
  // the endpoint paginates; if a record ever exceeds the page we ask for, the
  // page has to say so rather than quietly showing part of the register
  const hasUnloadedMatches = !!matchListData?.data?.next;

  // useQueries hands back a fresh array every render, so memoising on it
  // directly would never hit. dataUpdatedAt is what actually changes the
  // result: it moves both on the first load and on every later refetch, so
  // an invalidated-and-refetched payload (a new match decision, a new
  // approval comment) is picked up without waiting for a page reload.
  const payloadsVersion = payloads
    .map((payload) => `${payload.activityId}:${payload.dataUpdatedAt}`)
    .join();
  const loadedPayloads = useMemo(
    () =>
      payloads
        .filter((payload) => payload.data)
        .map(({ activityId, data }) => ({ activityId, data })),
    // payloadsVersion stands in for payloads (see above)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payloadsVersion],
  );

  const matches = useMemo(
    () =>
      buildMonitoringMatches(
        loadedPayloads,
        screeningActivities,
        accumulatedMatches,
      ),
    [loadedPayloads, screeningActivities, accumulatedMatches],
  );

  const newestActivityId = screeningActivities[0]?.id;
  const [localSelection, setLocalSelection] =
    useState<TimelineSelection | null>(null);
  const selection = controlled ? controlled.selection : localSelection;
  const setSelection = controlled ? controlled.onSelect : setLocalSelection;
  // null until the officer touches it, so the default can follow the data: on a
  // record with nothing outstanding, opening on "new results only" shows an
  // empty screen for no reason
  const [chosenLens, setChosenLens] = useState<ResultsLens | null>(null);
  // which run's body is on screen, per match. Absent means "the default for the
  // current selection", so switching timeline entry resets the strip naturally
  const [evidenceByMatchId, setEvidenceByMatchId] = useState<
    Record<number, number>
  >({});

  // defaults to the newest alert, which is why the officer is usually here
  const activeSelection: TimelineSelection =
    selection ?? newestActivityId ?? ALL_ACTIVITY;

  const selectedActivity =
    activeSelection === ALL_ACTIVITY
      ? null
      : screeningActivities.find(
          (activity) => activity.id === activeSelection,
        ) ?? null;

  const selectTimelineEntry = (next: TimelineSelection) => {
    setSelection(next);
    setEvidenceByMatchId({});
  };

  const selectEvidence = (matchId: number, activityId: number) =>
    setEvidenceByMatchId((current) => ({ ...current, [matchId]: activityId }));

  /** Matches the current timeline selection puts in scope. */
  const inScope = useMemo(() => {
    if (activeSelection === ALL_ACTIVITY) return matches;
    return matches.filter((match) =>
      match.contributions.some(
        (contribution) => contribution.activityId === activeSelection,
      ),
    );
  }, [matches, activeSelection]);

  // an excluded match needs no review, so new results on one are not
  // outstanding work and do not open the page on "new results only"
  const hasOutstanding = matches.some(
    (match) => match.hasNewResults && !match.isExcluded,
  );
  const lens: ResultsLens = chosenLens ?? (hasOutstanding ? "new" : "all");

  /** Applies the results lens on top of the selection. */
  const visible = useMemo(() => {
    const lensed =
      lens === "new" ? inScope.filter((match) => match.hasNewResults) : inScope;
    return orderMonitoringMatches(lensed);
  }, [inScope, lens]);

  /**
   * Matches on the record the current selection does not cover. Under a single
   * alert this is what the alert did not return; it always includes anything in
   * the accumulated list that no loaded activity accounts for, which is either
   * inherited from the initial screening or on an activity beyond the load
   * limit.
   */
  const notInScope = useMemo(() => {
    const inScopeIds = new Set(inScope.map((match) => match.id));
    const knownIds = new Set(matches.map((match) => match.id));

    const fromUnion = matches
      .filter((match) => !inScopeIds.has(match.id))
      .map((match) => ({
        id: match.id,
        name: match.body?.profile_details?.matched?.name?.full ?? null,
        matchId: match.body?.match_id ?? null,
        statusDisplay: match.statusDisplay,
        lastSeen: match.lastSeen,
        /** The alert to jump to, newest first. */
        jumpToActivityId: match.contributions[0]?.activityId ?? null,
      }));

    const unaccounted = accumulatedMatches
      .filter((match: any) => !knownIds.has(match?.id))
      .map((match: any) => ({
        id: match?.id,
        // no loaded payload carries this subject, so there is no name to show
        name: null,
        matchId: match?.match_id ?? null,
        statusDisplay: match?.status_display ?? null,
        lastSeen: null,
        jumpToActivityId: null,
      }));

    return [...fromUnion, ...unaccounted];
  }, [inScope, matches, accumulatedMatches]);

  /** The synthetic record ScreeningResultView renders. */
  const records = useMemo(() => {
    const bodyByMatchId = new Map<number, any>();

    visible.forEach((match: MonitoringMatch) => {
      const chosenActivityId =
        evidenceByMatchId[match.id] ??
        (activeSelection === ALL_ACTIVITY ? undefined : activeSelection);

      const contribution =
        match.contributions.find(
          (candidate) => candidate.activityId === chosenActivityId,
        ) ?? match.contributions[0];

      if (contribution) bodyByMatchId.set(match.id, contribution.body);
    });

    // under All activity no single run is in scope, so the submitted details
    // come from the monitoring record itself, which is what is being monitored
    const informationUsed =
      activeSelection === ALL_ACTIVITY
        ? informationUsedFromRecord(monitoring)
        : loadedPayloads.find(
            (payload) => payload.activityId === activeSelection,
          )?.data?.instance?.records?.[0]?.information_used ??
          informationUsedFromRecord(monitoring);

    return buildScreeningRecord(visible, informationUsed, bodyByMatchId);
  }, [
    visible,
    evidenceByMatchId,
    activeSelection,
    monitoring,
    loadedPayloads,
  ]);

  /** Which run each visible card is currently showing. */
  const selectedEvidenceFor = (match: MonitoringMatch) => {
    const chosen =
      evidenceByMatchId[match.id] ??
      (activeSelection === ALL_ACTIVITY ? undefined : activeSelection);

    return (
      match.contributions.find(
        (contribution) => contribution.activityId === chosen,
      ) ?? match.contributions[0]
    );
  };

  const matchById = useMemo(
    () => new Map(matches.map((match) => [match.id, match])),
    [matches],
  );

  const counts = useMemo(
    () =>
      decisionCounts(
        accumulatedMatches,
        new Set(
          matches.filter((match) => match.isExcluded).map((match) => match.id),
        ),
      ),
    [accumulatedMatches, matches],
  );

  return {
    isLoading: isLoadingPayloads || isLoadingMatchList,
    screeningActivities,
    /** Only the alerts whose payloads were actually fetched. */
    loadedActivities: screeningActivities.slice(
      0,
      screeningActivities.length - notLoadedCount,
    ),
    notLoadedCount,
    loadAllActivities,
    hasUnloadedMatches,
    /**
     * The match list (live decisions) is withheld by permission and there are
     * reviewable matches whose shown decision may therefore be stale.
     */
    isMatchListWithheld:
      !canViewMatchList && matches.some((match) => !match.isExcluded),
    failedActivityIds: payloads
      .filter((payload) => payload.isError)
      .map((payload) => payload.activityId),
    retryActivity: (activityId: number) =>
      payloads.find((payload) => payload.activityId === activityId)?.refetch(),

    activeSelection,
    selectedActivity,
    /** Who signed the selected alert off, when, and why. */
    selectedApprovalComment:
      activeSelection === ALL_ACTIVITY
        ? null
        : loadedPayloads.find(
            (payload) => payload.activityId === activeSelection,
          )?.data?.instance?.approval_comment ?? null,
    selectTimelineEntry,

    lens,
    setLens: setChosenLens,
    hasOutstanding,

    matches,
    matchById,
    inScope,
    visible,
    notInScope,
    records,
    counts,
    /** Matches on the record that need a decision; excluded ones are not. */
    accumulatedCount: counts.total,

    selectedEvidenceFor,
    selectEvidence,
  };
};
